/**
 * Wordmark loader + animator.
 *
 * Loads /disk-gelo-wordmark-clean.svg, injects it inline (so we can
 * animate every <path> individually), and exposes a play() method.
 *
 * Animation technique (adapted from a Framer Motion variant pattern):
 *   - Each <path> starts offset by (xOffset, yOffset) where the offset
 *     interpolates from (-20, +20) on the first path to (+20, -20) on
 *     the last — letters "converge" from opposite corners
 *   - Elastic-ish cubic-bezier easing on the way in
 *   - Tight stagger so the whole reveal lands in ~1.2s even with 67 paths
 *   - After the entrance, the SVG floats with a sin loop
 *   - mouseenter replays the entrance
 */
import { gsap } from 'gsap'

export interface WordmarkHandle {
  el: SVGSVGElement
  play(): void
  dispose(): void
}

const SVG_URL = '/disk-gelo-wordmark-clean.svg'

let cachedSvgText: string | null = null

async function fetchSvg(): Promise<string> {
  if (cachedSvgText) return cachedSvgText
  const res = await fetch(SVG_URL)
  if (!res.ok) throw new Error(`wordmark: failed to fetch SVG (${res.status})`)
  cachedSvgText = await res.text()
  return cachedSvgText
}

export async function mountWordmark(
  container: HTMLElement,
  opts: { reducedMotion: boolean },
): Promise<WordmarkHandle | null> {
  let svgText: string
  try {
    svgText = await fetchSvg()
  } catch (err) {
    console.warn('[wordmark]', err)
    return null
  }

  // Parse and adopt
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml')
  const svg = doc.documentElement as unknown as SVGSVGElement
  if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
    console.warn('[wordmark] root is not <svg>')
    return null
  }

  // Make it scale fluidly inside the container
  svg.removeAttribute('width')
  svg.removeAttribute('height')
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
  svg.style.width = '100%'
  svg.style.height = '100%'
  svg.style.display = 'block'
  svg.style.overflow = 'visible'
  svg.setAttribute('aria-label', 'Disk Gelo')
  svg.setAttribute('role', 'img')

  container.innerHTML = ''
  container.appendChild(svg)

  const paths = Array.from(svg.querySelectorAll('path')) as SVGPathElement[]
  if (paths.length === 0) return { el: svg, play: () => {}, dispose: () => {} }

  // Hint the compositor — we will be transforming + fading these
  for (const p of paths) {
    p.style.willChange = 'transform, opacity'
    p.style.transformBox = 'fill-box'
    p.style.transformOrigin = 'center'
  }

  let floatTween: gsap.core.Tween | null = null

  function startFloat() {
    if (floatTween) floatTween.kill()
    if (opts.reducedMotion) return
    floatTween = gsap.to(svg, {
      y: -6,
      duration: 3,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
    })
  }

  function play() {
    if (floatTween) {
      floatTween.kill()
      floatTween = null
    }
    gsap.set(svg, { y: 0 })

    if (opts.reducedMotion) {
      gsap.set(paths, { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 })
      return
    }

    const n = paths.length
    // Per-path entry offsets — convergence from opposite corners
    paths.forEach((p, i) => {
      const t = n === 1 ? 0.5 : i / (n - 1)
      const xOffset = -28 + 56 * t // -28 → +28
      const yOffset = 28 - 56 * t // +28 → -28
      gsap.set(p, {
        opacity: 0,
        x: xOffset,
        y: yOffset,
        scale: 0.7,
        rotate: (t - 0.5) * 12, // gentle counter-rotation
      })
    })

    gsap.to(paths, {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      duration: 0.65,
      ease: 'back.out(1.6)',
      stagger: {
        each: 0.011, // ~0.75s window for 67 paths → ~1.4s total entry
        from: 'start',
      },
      onComplete: startFloat,
    })
  }

  // Replay on hover
  function onEnter() {
    play()
  }
  svg.addEventListener('mouseenter', onEnter)

  return {
    el: svg,
    play,
    dispose() {
      if (floatTween) floatTween.kill()
      svg.removeEventListener('mouseenter', onEnter)
    },
  }
}
